import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const searchableText = (section) => [
  section.title,
  section.summary,
  section.description,
  ...(section.steps || []),
  ...(section.items || []).flatMap((item) => [item.title, item.body]),
].filter(Boolean).join(" ").toLowerCase();

export default function KnowledgeDirectory({ sections, placeholder, emptyMessage = "No guidance matches that search." }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sections;
    return sections.filter((section) => searchableText(section).includes(needle));
  }, [query, sections]);

  return (
    <div>
      <div className="relative max-w-2xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className="h-11 pl-10"
          aria-label={placeholder}
        />
      </div>

      {filtered.length ? (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {filtered.map((section) => (
            <section key={section.id} id={section.id} className="self-start rounded-lg border border-border bg-card p-5 scroll-mt-24">
              <h2 className="font-display text-xl font-black">{section.title}</h2>
              {(section.summary || section.description) && (
                <p className="mt-1 text-sm text-muted-foreground">{section.summary || section.description}</p>
              )}

              {section.items ? (
                <Accordion type="single" collapsible className="mt-3">
                  {section.items.map((item, index) => (
                    <AccordionItem key={`${section.id}-${index}`} value={`${section.id}-${index}`}>
                      <AccordionTrigger className="font-bold">{item.title}</AccordionTrigger>
                      <AccordionContent className="leading-6 text-muted-foreground">{item.body}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <ol className="mt-4 space-y-3">
                  {(section.steps || []).map((step, index) => (
                    <li key={`${section.id}-step-${index}`} className="flex gap-3 text-sm leading-6">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              )}

              {section.route && (
                <Button asChild variant="outline" size="sm" className="mt-5 rounded-full">
                  <Link to={section.route}>{section.routeLabel || "Open section"}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              )}
            </section>
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}
